import { FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Checkbox } from "primereact/checkbox";
import { ColorPicker } from "primereact/colorpicker";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { TabPanel, TabView } from "primereact/tabview";
import { classNames } from "primereact/utils";

import { normalizeHex, normalizeHexOrLinearGradient } from "../../../../../../../utils/functions/normalizeHex";
import { isValidHttpUrl } from "../../../../../../../utils/functions/validateHttpUrl";
import { translationKeys } from "../../../../../../models/app-translation-keys";
import {
  AppLanguageOptionsSubset,
  AppLanguageOptionsSubsetEnum,
} from "../../../../../../models/constants/app-language-options.const";
import { useSharedDialogState } from "../../../../../Shared/Components/Dialogs/SharedDialog";
import {
  BUTTON_TEXT_MAX_LENGTH,
  DEFAULT_TEXT_COLOR,
  DESCRIPTION_MAX_LENGTH,
  IFeatureTileEditorDialogState,
  TITLE_MAX_LENGTH,
} from "../featureTileEditorDialogTypes";

import "./FeatureTileEditorDialogContent.scss";

type IconOption = { label: string; value: string };

const featureKeys = translationKeys.adminArea.featureTiles;

export const FeatureTileEditorDialogContent: FunctionComponent<{ disabled: boolean }> = ({ disabled }) => {
  const { t, i18n } = useTranslation();
  const { sharedDialogState, setSharedDialogState } = useSharedDialogState<IFeatureTileEditorDialogState>();
  const isDisabled = disabled || sharedDialogState.isSaving;

  const iconOptions: IconOption[] = useMemo(
    () => [
      {
        label: t(featureKeys.fields.externalLinkOption, { defaultValue: "External link" }),
        value: "externalLink",
      },
    ],
    [i18n.language],
  );

  const colorText = useMemo(() => normalizeHex(sharedDialogState.colorTextRaw), [sharedDialogState.colorTextRaw]);
  const colorBackground = useMemo(
    () => normalizeHexOrLinearGradient(sharedDialogState.colorBackgroundRaw),
    [sharedDialogState.colorBackgroundRaw],
  );
  const colorBackgroundHover = useMemo(
    () => normalizeHexOrLinearGradient(sharedDialogState.colorBackgroundHoverRaw),
    [sharedDialogState.colorBackgroundHoverRaw],
  );

  const invalidUrlLangs = useMemo(() => {
    const bad: AppLanguageOptionsSubsetEnum[] = [];

    for (const lang of AppLanguageOptionsSubset) {
      const value = (sharedDialogState.externalLinkUrl?.[lang] ?? "").trim();
      if (value && !isValidHttpUrl(value)) {
        bad.push(lang);
      }
    }

    return bad;
  }, [sharedDialogState.externalLinkUrl]);

  const isTextColorInvalid = !!sharedDialogState.colorTextRaw.trim() && colorText === undefined;
  const isBgColorInvalid = !!sharedDialogState.colorBackgroundRaw.trim() && colorBackground === undefined;
  const isBgColorHoverInvalid =
    !!sharedDialogState.colorBackgroundHoverRaw.trim() && colorBackgroundHover === undefined;

  const updateState = (partial: Partial<IFeatureTileEditorDialogState>): void => {
    setSharedDialogState((previousState: IFeatureTileEditorDialogState) => ({
      ...previousState,
      ...partial,
    }));
  };

  const updateMultiLang = (
    field: "title" | "description" | "buttonText" | "buttonTooltip" | "externalLinkUrl",
    lang: AppLanguageOptionsSubsetEnum,
    value: string,
  ): void => {
    setSharedDialogState((previousState: IFeatureTileEditorDialogState) => ({
      ...previousState,
      [field]: {
        ...(previousState[field] ?? {}),
        [lang]: value,
      },
    }));
  };

  return (
    <div className="feature-tile-editor">
      <div className="feature-tile-editor-grid">
        <div className="form-element">
          <label>{t(featureKeys.fields.enabled)}</label>

          <Checkbox
            disabled={isDisabled}
            checked={!!sharedDialogState.enabled}
            onChange={e => updateState({ enabled: !!e.checked })}
          />
        </div>

        <div className="form-element">
          <label>{t(featureKeys.fields.icon)}</label>

          <Dropdown
            disabled={isDisabled}
            value={sharedDialogState.iconKey}
            options={iconOptions}
            onChange={e => updateState({ iconKey: e.value })}
            placeholder={t(featureKeys.fields.iconPlaceholder)}
          />
        </div>

        <div className="form-element">
          <label>{t(featureKeys.fields.colorText)}</label>

          <div className="color-row">
            <ColorPicker
              disabled={isDisabled}
              value={(colorText ?? DEFAULT_TEXT_COLOR).replace("#", "")}
              onChange={e => updateState({ colorTextRaw: `#${String(e.value).toUpperCase()}` })}
              format="hex"
            />

            <InputText
              disabled={isDisabled}
              value={sharedDialogState.colorTextRaw}
              onChange={e => updateState({ colorTextRaw: e.target.value })}
              className={classNames({ "p-invalid": isTextColorInvalid })}
              placeholder="#1F2D3D"
            />
          </div>

          {isTextColorInvalid && <small className="p-error">{t(featureKeys.validation.colorInvalid)}</small>}
        </div>

        <div className="form-element">
          <label>{t(featureKeys.fields.colorBackground)}</label>

          <div className="color-row">
            <ColorPicker
              disabled={isDisabled}
              value={(normalizeHex(sharedDialogState.colorBackgroundRaw) ?? "FFFFFF").replace("#", "")}
              onChange={e => updateState({ colorBackgroundRaw: `#${String(e.value).toUpperCase()}` })}
              format="hex"
            />

            <InputText
              disabled={isDisabled}
              value={sharedDialogState.colorBackgroundRaw}
              onChange={e => updateState({ colorBackgroundRaw: e.target.value })}
              className={classNames({ "p-invalid": isBgColorInvalid })}
              placeholder="#FFFFFF"
            />
          </div>
          {isBgColorInvalid && <small className="p-error">{t(featureKeys.validation.colorInvalid)}</small>}
        </div>

        <div className="form-element">
          <label>{t(featureKeys.fields.colorBackgroundHover)}</label>

          <div className="color-row">
            <ColorPicker
              disabled={isDisabled}
              value={(normalizeHex(sharedDialogState.colorBackgroundHoverRaw) ?? "FFFFFF").replace("#", "")}
              onChange={e => updateState({ colorBackgroundHoverRaw: `#${String(e.value).toUpperCase()}` })}
              format="hex"
            />

            <InputText
              disabled={isDisabled}
              value={sharedDialogState.colorBackgroundHoverRaw}
              onChange={e => updateState({ colorBackgroundHoverRaw: e.target.value })}
              className={classNames({ "p-invalid": isBgColorHoverInvalid })}
              placeholder="#FFFFFF"
            />
          </div>
          {isBgColorHoverInvalid && <small className="p-error">{t(featureKeys.validation.colorInvalid)}</small>}
        </div>
      </div>

      <div className="form-element form-element-wide">
        <label>{t(featureKeys.fields.texts)}</label>

        <TabView>
          {AppLanguageOptionsSubset.map(lang => {
            const titleValueLength = (sharedDialogState.title?.[lang] ?? "").trim().length;
            const descriptionValueLength = (sharedDialogState.description?.[lang] ?? "").trim().length;
            const buttonTextValueLength = (sharedDialogState.buttonText?.[lang] ?? "").trim().length;
            const isTitleValueInvalid = titleValueLength === 0;
            const isTitleValueTooLong = titleValueLength > TITLE_MAX_LENGTH;
            const isDescriptionValueInvalid = descriptionValueLength === 0;
            const isDescriptionValueTooLong = descriptionValueLength > DESCRIPTION_MAX_LENGTH;
            const isButtonTextValueInvalid = buttonTextValueLength === 0;
            const isButtonTextValueTooLong = buttonTextValueLength > BUTTON_TEXT_MAX_LENGTH;
            const isButtonTooltipValueInvalid = (sharedDialogState.buttonTooltip?.[lang] ?? "").trim().length === 0;
            const urlValue = sharedDialogState.externalLinkUrl?.[lang] ?? "";
            const urlInvalid = invalidUrlLangs.includes(lang);
            const urlRequiredError = (sharedDialogState.externalLinkUrl?.[lang] ?? "").trim().length === 0;

            return (
              <TabPanel key={lang} header={lang.toUpperCase()}>
                <div className="feature-tile-editor-grid">
                  <div className="form-element">
                    <label>{t(featureKeys.fields.title)}</label>

                    <InputTextarea
                      disabled={isDisabled}
                      value={sharedDialogState.title?.[lang] ?? ""}
                      onChange={e => updateMultiLang("title", lang, e.target.value)}
                      className={classNames({ "p-invalid": isTitleValueInvalid || isTitleValueTooLong })}
                      rows={3}
                    />

                    {isTitleValueInvalid && (
                      <small className="p-error">{t(featureKeys.validation.titleRequired)}</small>
                    )}

                    {!isTitleValueInvalid && isTitleValueTooLong && (
                      <small className="p-error">
                        {t(featureKeys.validation.titleMaxLength, {
                          defaultValue: `Title must be at most ${TITLE_MAX_LENGTH} characters.`,
                          max: TITLE_MAX_LENGTH,
                        })}
                      </small>
                    )}
                  </div>

                  <div className="form-element">
                    <label>{t(featureKeys.fields.description)}</label>

                    <InputTextarea
                      value={sharedDialogState.description?.[lang] ?? ""}
                      onChange={e => updateMultiLang("description", lang, e.target.value)}
                      className={classNames({ "p-invalid": isDescriptionValueInvalid || isDescriptionValueTooLong })}
                      rows={3}
                      disabled={isDisabled}
                    />

                    {isDescriptionValueInvalid && (
                      <small className="p-error">{t(featureKeys.validation.descriptionRequired)}</small>
                    )}

                    {!isDescriptionValueInvalid && isDescriptionValueTooLong && (
                      <small className="p-error">
                        {t(featureKeys.validation.descriptionMaxLength, {
                          defaultValue: `Description must be at most ${DESCRIPTION_MAX_LENGTH} characters.`,
                          max: DESCRIPTION_MAX_LENGTH,
                        })}
                      </small>
                    )}
                  </div>

                  <div className="form-element">
                    <label>{t(featureKeys.fields.buttonText)}</label>

                    <InputText
                      disabled={isDisabled}
                      value={sharedDialogState.buttonText?.[lang] ?? ""}
                      onChange={e => updateMultiLang("buttonText", lang, e.target.value)}
                      className={classNames({ "p-invalid": isButtonTextValueInvalid || isButtonTextValueTooLong })}
                    />

                    {isButtonTextValueInvalid && (
                      <small className="p-error">{t(featureKeys.validation.buttonTextRequired)}</small>
                    )}

                    {!isButtonTextValueInvalid && isButtonTextValueTooLong && (
                      <small className="p-error">
                        {t(featureKeys.validation.buttonTextMaxLength, {
                          defaultValue: `Button text must be at most ${BUTTON_TEXT_MAX_LENGTH} characters.`,
                          max: BUTTON_TEXT_MAX_LENGTH,
                        })}
                      </small>
                    )}
                  </div>

                  <div className="form-element">
                    <label>{t(featureKeys.fields.buttonTooltip)}</label>

                    <InputText
                      value={sharedDialogState.buttonTooltip?.[lang] ?? ""}
                      onChange={e => updateMultiLang("buttonTooltip", lang, e.target.value)}
                      className={classNames({ "p-invalid": isButtonTooltipValueInvalid })}
                      disabled={isDisabled}
                    />

                    {isButtonTooltipValueInvalid && (
                      <small className="p-error">{t(featureKeys.validation.buttonTooltipRequired)}</small>
                    )}
                  </div>

                  <div className="form-element">
                    <label>{t(featureKeys.fields.externalLinkUrl)}</label>

                    <InputText
                      disabled={isDisabled}
                      value={urlValue}
                      onChange={e => updateMultiLang("externalLinkUrl", lang, e.target.value)}
                      className={classNames({ "p-invalid": urlInvalid || urlRequiredError })}
                      placeholder="https://example.com"
                    />

                    {urlRequiredError && <small className="p-error">{t(featureKeys.validation.urlRequired)}</small>}

                    {!urlRequiredError && urlInvalid && (
                      <small className="p-error">{t(featureKeys.validation.urlInvalid)}</small>
                    )}
                  </div>
                </div>
              </TabPanel>
            );
          })}
        </TabView>
      </div>
    </div>
  );
};
